import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { getOnlyToken } from "../../utils/commonFunctions";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const Invoice = () => {
    const [invoiceData, setInvoiceData] = useState(null);
    const location = useLocation();
    const invoiceId = location.state?.invoice_id;
    const invoiceRef = useRef(null);
    const navigate = useNavigate();

    // Axios GET method
    const fetchPaymentData = async () => {
        const token = getOnlyToken();
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        };
        try {
            const response = await axios.get(
                `/api/billing/get-payment-data/?invoice_id=${invoiceId}`,
                config
            );
            setInvoiceData(response.data?.results?.data || null);
        } catch (error) {
            console.error("Error fetching payment data:", error);
            return null;
        }
    };


    // Download Invoice as PDF
    const downloadInvoice = async () => {
        if (!invoiceRef.current) return;

        const element = invoiceRef.current;
        
        // Create a clone of the element for styling
        const clonedElement = element.cloneNode(true);
        
        // Apply PDF-specific styles to the cloned element
        clonedElement.style.backgroundColor = '#ffffff';
        clonedElement.style.color = '#000000';
        clonedElement.style.fontFamily = 'Arial, sans-serif';
        clonedElement.style.fontSize = '14px';
        clonedElement.style.lineHeight = '1.6';
        clonedElement.style.padding = '20px';
        
        // Style all text elements for better PDF visibility
        const allElements = clonedElement.querySelectorAll('*');
        allElements.forEach(el => {
            if (el.style) {
                el.style.color = '#000000';
                if (el.tagName === 'TH' || el.tagName === 'TD') {
                    el.style.padding = '12px 8px';
                    el.style.border = '1px solid #dee2e6';
                }
                if (el.tagName === 'TABLE') {
                    el.style.borderCollapse = 'collapse';
                    el.style.width = '100%';
                }
                if (el.tagName === 'H2' || el.tagName === 'H4') {
                    el.style.fontWeight = 'bold';
                }
            }
        });
        
        // Temporarily add to DOM for rendering
        document.body.appendChild(clonedElement);
        clonedElement.style.position = 'absolute';
        clonedElement.style.left = '-9999px';
        
        // Enhanced canvas options for better quality
        const canvas = await html2canvas(clonedElement, { 
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            width: clonedElement.scrollWidth,
            height: clonedElement.scrollHeight
        });
        
        // Remove cloned element
        document.body.removeChild(clonedElement);
        
        const imgData = canvas.toDataURL("image/png", 1.0);

        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
        }

        pdf.save(`invoice_${invoiceData?.invoice_number || "file"}.pdf`);
    };

    const sendInvoice = async () => {
        if (!invoiceRef.current || !invoiceId) return;
        try {
            const element = invoiceRef.current;
            
            // Create a clone of the element for styling
            const clonedElement = element.cloneNode(true);
            
            // Apply PDF-specific styles to the cloned element
            clonedElement.style.backgroundColor = '#ffffff';
            clonedElement.style.color = '#000000';
            clonedElement.style.fontFamily = 'Arial, sans-serif';
            clonedElement.style.fontSize = '14px';
            clonedElement.style.lineHeight = '1.6';
            clonedElement.style.padding = '20px';
            
            // Style all text elements for better PDF visibility
            const allElements = clonedElement.querySelectorAll('*');
            allElements.forEach(el => {
                if (el.style) {
                    el.style.color = '#000000';
                    if (el.tagName === 'TH' || el.tagName === 'TD') {
                        el.style.padding = '12px 8px';
                        el.style.border = '1px solid #dee2e6';
                    }
                    if (el.tagName === 'TABLE') {
                        el.style.borderCollapse = 'collapse';
                        el.style.width = '100%';
                    }
                    if (el.tagName === 'H2' || el.tagName === 'H4') {
                        el.style.fontWeight = 'bold';
                    }
                }
            });
            
            // Temporarily add to DOM for rendering
            document.body.appendChild(clonedElement);
            clonedElement.style.position = 'absolute';
            clonedElement.style.left = '-9999px';
            
            // Enhanced canvas options for better quality
            const canvas = await html2canvas(clonedElement, { 
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false,
                width: clonedElement.scrollWidth,
                height: clonedElement.scrollHeight
            });
            
            // Remove cloned element
            document.body.removeChild(clonedElement);
            
            const imgData = canvas.toDataURL("image/png", 1.0);
            const pdf = new jsPDF("p", "mm", "a4");
            const imgWidth = 210;
            const pageHeight = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }
            // Get PDF as base64 string (clean base64 without data URI prefix)
            const pdfBase64 = pdf.output('datauristring').split(',')[1];

            const token = getOnlyToken();
            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            };
            const payload = {
                file_data: pdfBase64,
                invoice_id: invoiceId
            };
            await axios.post('/api/billing/send-file-email/', payload, config);
            alert('Invoice sent successfully!');
        } catch (error) {
            alert('Failed to send invoice.');
            console.error('Send invoice error:', error);
        }
    };

    useEffect(() => {
        fetchPaymentData();
    }, []);

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box">
                        <h4 className="page-title lh-1 mb-3">Payment Statement</h4>
                    </div>
                </div>

                <div className="col-xl-12">
                    <div className="card">
                        <div className="card-body" ref={invoiceRef}>
                            {/* Header */}
                            <div className="clearfix">
                                <div className="float-start mb-3">
                                    <img
                                        src="assets/images/logo.png"
                                        alt="dark logo"
                                        height="70"
                                    />
                                </div>
                                <div className="float-end">
                                    <h2 className="m-0 d-print-none">Invoice</h2>
                                </div>
                            </div>

                            {/* Company & Billing Info */}
                            <div className="row mt-4">
                                <div className="col-md-4">
                                    <h4 className="text-info">Pranathi Software Services,</h4>
                                    <address className="fs-15">
                                        295 Durham Avenue, Suite D
                                        <br />
                                        South Plainfield, NJ 07080
                                        <br />
                                        <abbr>Phone :</abbr> (732) 444-3132
                                        <br />
                                        <abbr>Email :</abbr> info@vitelglobal.com
                                    </address>
                                </div>

                                <div className="col-md-4">
                                    <h4 className="text-info">Billed To</h4>
                                    <address className="fs-15">
                                        Nizamabad, Telangana, India
                                        <br />
                                        <abbr>Phone :</abbr> (732) 444-3132
                                        <br />
                                        <abbr>Email :</abbr> shailajabelgula0204@gmail.com
                                    </address>
                                </div>

                                <div className="col-md-4">
                                    <div className="float-sm-end">
                                        <p className="fs-15 mb-1">
                                            <strong>Order Date : </strong>
                                            <span className="float-end ms-5">
                                                {invoiceData?.invoice_date || "--"}
                                            </span>
                                        </p>
                                        <p className="fs-15 mb-1">
                                            <strong>Order Status : </strong>
                                            <span className="badge bg-success float-end">
                                                {invoiceData?.invoice_status || "--"}
                                            </span>
                                        </p>
                                        <p className="fs-15 mb-1">
                                            <strong>Invoice :</strong>{" "}
                                            <span className="float-end">
                                                #{invoiceData?.invoice_number || "--"}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Invoice Table */}
                            <div className="row">
                                <div className="col-12">
                                    <div className="table-responsive">
                                        <table className="table table-sm text-nowrap table-centered table-borderless mb-0 mt-3 fs-15">
                                            <thead className="border-top border-bottom bg-light-subtle border-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Description</th>
                                                    <th>No. of License</th>
                                                    <th>Subscription Period</th>
                                                    <th>Subscription Plan</th>
                                                    <th>Rate</th>
                                                    <th>Tax</th>
                                                    <th className="text-end">Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoiceData ? (
                                                    <>
                                                        <tr>
                                                            <td>1</td>
                                                            <td>{invoiceData.plan_option.description}</td>
                                                            <td>{invoiceData.billing_months || "01"}</td>
                                                            <td>{invoiceData.plan_option.subscription_period}</td>
                                                            <td>{invoiceData.plan_option.plan_type}</td>
                                                            <td>${invoiceData.plan_option.invoice_amount}</td>
                                                            <td>${invoiceData.plan_option.plan_tax_amount}</td>
                                                            <td className="text-end">
                                                                ${invoiceData.plan_option.total_amount}
                                                            </td>
                                                        </tr>
                                                        {invoiceData.discount_status === "Y" && (
                                                            <tr>
                                                                <td colSpan="6"></td>
                                                                <td>
                                                                    <p className="mb-0 fw-semibold fs-15">
                                                                        Discount ({invoiceData.discount_percentage}%):
                                                                    </p>
                                                                </td>
                                                                <td className="text-end fw-semibold fs-15">
                                                                    -${invoiceData.discount_amount}
                                                                </td>
                                                            </tr>
                                                        )}
                                                        <tr>
                                                            <td colSpan="6"></td>
                                                            <td>
                                                                <p className="mb-0 fw-semibold fs-15">
                                                                    Sub Total :
                                                                </p>
                                                            </td>
                                                            <td className="text-end fw-semibold fs-15">
                                                                ${invoiceData.invoice_amount}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td colSpan="6"></td>
                                                            <td>
                                                                <p className="mb-0 fw-semibold fs-15">Tax :</p>
                                                            </td>
                                                            <td className="text-end fw-semibold fs-15">
                                                                ${invoiceData.plan_tax_amount}
                                                            </td>
                                                        </tr>
                                                        <tr
                                                            style={{
                                                                whiteSpace: "nowrap",
                                                                fontWeight: 700,
                                                                color: "rgb(255, 152, 0)",
                                                                fontSize: "17px",
                                                            }}
                                                        >
                                                            <td colSpan="6"></td>
                                                            <td
                                                                style={{
                                                                    border: "1px solid",
                                                                    borderLeft: "none",
                                                                    borderRight: "none",
                                                                }}
                                                            >
                                                                Total :
                                                            </td>
                                                            <td
                                                                className="text-end"
                                                                style={{
                                                                    border: "1px solid",
                                                                    borderLeft: "none",
                                                                    borderRight: "none",
                                                                }}
                                                            >
                                                                ${invoiceData.plan_option.total_amount}
                                                            </td>
                                                        </tr>
                                                    </>
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center">
                                                            Loading...
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="row">
                                <div className="clearfix pt-3">
                                    <h4>Notes:</h4>
                                    <p className="mb-1">
                                        For any invoice or billing questions, contact our
                                        accounts team at info@pranathiss.com, +1 732 333 3037.
                                    </p>
                                    <p
                                        className="fw-semibold fs-15"
                                        style={{
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            color: "rgb(255, 152, 0)",
                                            margin: 0,
                                        }}
                                    >
                                        Thank you for choosing Pranathi Software Services
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="d-print-none mt-4">
                            <div className="invoice-btn-section d-print-none mb-2 col-md-12">
                                <div className="d-flex flex-wrap gap-2 justify-content-center">
                                    <button className="btn btn-primary mb-3" onClick={sendInvoice}>
                                        <i className="ri-mail-send-line"></i> Send Invoice
                                    </button>
                                    <button
                                        className="btn btn-warning mb-3"
                                        onClick={() => window.print()}
                                    >
                                        <i className="ri-printer-line"></i> Print Invoice
                                    </button>
                                    <button
                                        className="btn btn-primary mb-3"
                                        onClick={downloadInvoice}
                                    >
                                        Download Invoice
                                    </button>
                                    <button
                                        className="btn btn-soft-danger mb-3"
                                        onClick={() => navigate("/paymenthistory")}
                                    >
                                        <i className="ri-arrow-go-back-line"></i> Back
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Invoice;
